import { Token } from "./TokenEnum";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { GlobalStore } from "./GlobalStore";
import { API_BASE_URL, API_VERSION } from "../../config/env";
import * as Sentry from "@sentry/browser";
import { NETWORK_TO_PRICE_ENDPOINT_MAP } from "../../utils/bootstrapWagmi";
import { Amount } from "../../types/amount";
import { Address } from "@wagmi/core";
import { size } from "lodash-es";
import { extractTokenList, transformPricesToBalances } from "../../utils/tokenMetadataUtils";
import { z } from "zod";
import { tokensApiResponseSchema } from "../../generated-schemas";

type TokenType = "alt" | "stablecoin" | "major";

/**
 * 0 - No permit
 * 1 - Permit (Domain with version and chainId)
 * 2 - Permit (Domain without version, but with chainId )
 * 3 - Permit (Domain with version and salt)
 * 4 - Permit (Domain with version, chainid and salt) (
 */
export enum PermitDomain {
  NONE = "NONE",
  WITH_VERSION_AND_CHAINID = "WITH_VERSION_AND_CHAINID",
  WITH_CHAINID = "WITH_CHAINID",
  WITH_VERSION_AND_SALT = "WITH_VERSION_AND_SALT",
  WITH_VERSION_AND_CHAINID_AND_SALT = "WITH_VERSION_AND_CHAINID_AND_SALT",
}

export interface TokenData {
  name: string;
  cid: string;
  ticker: string;
  tokenType: TokenType;
  permitDomain: PermitDomain;
  iconUrl: string;
  minOrderSize?: number;
  industry: string;
  colour: string;
  availability: TokenAvailability;
  priceUsd: Amount;
  chainInfo: Array<ChainSupport>;
  buy: boolean;
  sell: boolean;
  displayDecimals: number | null;
}

export type TokenMetadata = {
  [key in Token]?: TokenData;
};

export interface TokenAvailability {
  isAvailable: boolean;
  canBuy: boolean;
  canSell: boolean;
}

export interface ChainSupport {
  chainId: number;
  contractAddress: Address;
  decimals: number;
}

export interface TokenMetadataResponse {
  tokens: TokenMetadata;
  metadata: {
    lastUpdate: number;
  };
}

export const fetchTokens = (
  networkName: string,
  includeInactiveTokens: boolean
): Promise<unknown> => {
  return fetch(
    `${API_BASE_URL}/${NETWORK_TO_PRICE_ENDPOINT_MAP[networkName]}/${API_VERSION}/tokens?${
      includeInactiveTokens ? "active_only=false" : ""
    }`
  ).then(response => {
    if (response.ok) {
      return response.json();
    } else {
      console.error(`Could not fetch token metadata. `, response.statusText);
      Sentry.captureException(`Could not fetch Token Metadata`, {
        extra: { error: response },
      });
      return {
        tokens: {},
      };
    }
  });
};

const parsePermitDomain = (permitDomain: number): PermitDomain => {
  switch (permitDomain) {
    case 0:
      return PermitDomain.NONE;
    case 1:
      return PermitDomain.WITH_VERSION_AND_CHAINID;
    case 2:
      return PermitDomain.WITH_CHAINID;
    case 3:
      return PermitDomain.WITH_VERSION_AND_SALT;
    case 4:
      return PermitDomain.WITH_VERSION_AND_CHAINID_AND_SALT;
    default:
      return PermitDomain.NONE;
  }
};

const transformTokenMetadataResponse = (
  tokenMetadataResponse: z.infer<typeof tokensApiResponseSchema>
): TokenMetadata => {
  console.log(`Transforming response`, tokenMetadataResponse);
  const metadata = Object.fromEntries(
    Object.entries(tokenMetadataResponse.tokens).map(([key, value]) => {
      let priceUsd = new Amount("0");
      try {
        if (value.priceUsd !== null) {
          priceUsd = new Amount(value.priceUsd?.toString() ?? "0");
        }
      } catch (e) {
        console.error(`Could not parse priceUsd for token ${key}`, e);
      }
      const DEFAULT_DISPLAY_DECIMALS = 5;
      if (!value.displayDecimals) {
        Sentry.captureException(`Token ${key} does not have displayDecimals set`);
      }
      const tokenData: TokenData = {
        name: value.name,
        cid: value.cid,
        ticker: value.ticker,
        tokenType: value.tokenType as TokenType,
        permitDomain: parsePermitDomain(value.permitDomain),
        iconUrl: value.iconUrl,
        minOrderSize: value.minOrderSize ?? 0,
        industry: value.industry,
        colour: value.colour ?? "#ffffff",
        availability: value.availability,
        priceUsd: priceUsd,
        chainInfo: value.chainInfo as Array<ChainSupport>,
        buy: value.availability.canBuy ?? false,
        sell: value.availability.canSell ?? false,
        displayDecimals: value.displayDecimals ?? DEFAULT_DISPLAY_DECIMALS,
      };
      return [key as Token, tokenData];
    })
  );
  return metadata;
};

const validateTokenMetadataResponse = (tokenMetadataResponse: unknown) => {
  console.log(tokenMetadataResponse, "tokenMetadataResponse");
  const result = tokensApiResponseSchema.safeParse(tokenMetadataResponse);
  return result;
};

/*
 * 1. Fetch tokens
 * 2. Validate response
 *  2.1 If invalid, return null
 *  2.2 If valid, transform response
 */
export const getTokens = async (networkName: string, includeInactiveTokens: boolean) => {
  const response = await fetchTokens(networkName, includeInactiveTokens);
  const validationResult = validateTokenMetadataResponse(response);
  if (validationResult.success) {
    const metadata = validationResult.data;
    // Potentially there might be issues with transforming it even if it's a valid response?
    const tokensMetadata = transformTokenMetadataResponse(metadata);
    return tokensMetadata;
  } else {
    console.log(`Invalid response from tokens endpoint`);
    console.error(validationResult.error.format());
    return null;
  }
};

export const getTokenMetadata = createAsyncAction(
  async (networkName: string) => {
    try {
      const result = await getTokens(networkName, false);

      if (result) {
        return successResult(result);
      }
    } catch (e) {
      console.log(e);
      Sentry.captureException(`Could not get tokens`, { extra: { event: e } });
      return errorResult([], `Could not get tokens for network ${networkName}`, e);
    }
    Sentry.captureException(`Could not get tokens`);
    return errorResult([], `Couldn't get tokens for network: ${networkName}`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error) {
        GlobalStore.update(s => {
          if (size(result.payload) === 0) {
            console.log(`Got an empty response from the Tokens endpoint - dropping it`);
            Sentry.captureException(`tokens endpoint returned an empty response`);
            return;
          }
          const metadataChainId = Object.values(result.payload)[0].chainInfo[0].chainId;
          if (s.network.id !== metadataChainId) {
            console.log(
              `Received Metadata response for a no-longer applicable network, ignoring it.`
            );
            return;
          }
          s.tokens = result.payload;
          console.log(result.payload, "tokens");
          s.tokenList = extractTokenList(result.payload);
          if (s.balances.length === 0) {
            s.balances = transformPricesToBalances(result.payload);
          }
        });
      }
    },
  }
);
